import { Typography } from '@mentalgrowth/ui-kit-web';
import classNames from 'classnames';
import { memo, useMemo } from 'react';
import 'react-loading-skeleton/dist/skeleton.css';

import { useFeature } from '@features/FeatureToggle';
import {
  CardAnnotation,
  LearnMoreList,
  type TpStripePlanCardProps,
  TwelveWeek,
} from '@features/Stripe';
import { FormattedMessage } from '@features/intl';
import { UiKitButton } from 'src/components/common/ui-kit-button';
import { currencyReplacer } from 'src/utils/payment/currency-replacer';
import { SubscriptionCardMaster } from '../master';
import { CurrentPrice } from '../shared/current-price';
import { DiscountPercentage } from '../shared/discount-percentage';
import { FullPrice } from '../shared/full-price';
import { PriceUnitFull } from '../shared/price-unit-full';

import IconChevronDown from '../../../../../../public/icons/chevron-down.svg';

import styles from './subscription-plan-card.module.scss';

export const SubscriptionPlanCard = memo((props: TpStripePlanCardProps) => {
  const {
    getPlanDisabled,
    handlePlanSelect,
    submitButtonText,
    inputIdPrefix,
    plan,
    wasPaymentTried,
    planCardClassname,
    learnMoreList,
    variant,
  } = props;

  const isIntroOfferEnabled = useFeature('introOffer');
  const currencySymbol = currencyReplacer(plan?.currency || 'usd');

  const dataMemoized = useMemo(
    () =>
      isIntroOfferEnabled
        ? {
            fullPrice: plan?.introOfferData?.fullPrice,
            fullPriceDiscount: plan?.introOfferData?.fullPriceDiscount,
          }
        : {
            fullPrice: plan?.fullPrice,
            fullPriceDiscount: plan?.fullPriceDiscount,
          },
    [
      isIntroOfferEnabled,
      plan?.introOfferData,
      plan?.fullPrice,
      plan?.fullPriceDiscount,
    ],
  );

  // @ts-ignore
  return (
    //@ts-ignore
    <SubscriptionCardMaster
      {...props}
      inputIdPrefix={inputIdPrefix}
      planCardClassname={classNames(styles.planCard, planCardClassname)}
      contentClassname={styles.content}
      leftBottomContent={(value: boolean) => (
        <div className={styles.wrapper}>
          <div
            className={classNames(styles.text, {
              [styles.textChecked]: value,
            })}
          >
            {plan?.fractionalPrice !== plan?.fractionalPriceDiscount && (
              <FullPrice
                currencySymbol={currencySymbol}
                price={dataMemoized.fullPrice}
              />
            )}{' '}
            <FullPrice
              price={dataMemoized.fullPriceDiscount}
              currencySymbol={currencySymbol}
              withDiscount
            />
          </div>
          <div className={styles.discountPercentage}>
            {wasPaymentTried && plan.preselected && (
              <del className={styles.defaultDiscount}>
                {plan.specialDiscountPercentage}%
              </del>
            )}
            {plan?.discountPercentage !== 0 &&
              plan?.discountPercentage != null && (
                <DiscountPercentage
                  discountPercentage={plan.discountPercentage || 0}
                  isActive={value}
                  wasPaymentTried={wasPaymentTried}
                  size="small"
                />
              )}
          </div>
        </div>
      )}
      rightContent={(value: boolean) => (
        <div className={styles.alignEnd}>
          <div className={styles.rightBlockContainer}>
            {plan?.fractionalPrice !== plan?.fractionalPriceDiscount && (
              <PriceUnitFull
                //@ts-ignore
                plan={plan}
                checked={value}
                className={styles.priceUnitFull}
              />
            )}
            <CurrentPrice plan={plan} checked={value} />
          </div>
        </div>
      )}
      fullWidthBottomContent={(value: boolean) => (
        <div className={styles.bottomContent}>
          <div className={styles.learnMoreHeader}>
            <Typography as="p" variant="body2-bold">
              <FormattedMessage
                id="Onboarding.Paywall.Main.Subtitle"
                defaultMessage="Learn more"
              />
            </Typography>
            <IconChevronDown
              className={classNames(styles.collapseIcon, {
                [styles.unCollapsed]: value,
              })}
            />
          </div>
          {value && (plan?.learnMoreList || learnMoreList) && (
            <>
              {plan?.learnMoreList && (
                <LearnMoreList learnMoreList={plan.learnMoreList} />
              )}
              {learnMoreList && <LearnMoreList learnMoreList={learnMoreList} />}
            </>
          )}

          {plan.name === '12-WEEK PLAN' && <TwelveWeek isChecked={value} />}

          {value && (
            <UiKitButton
              data-testid="get-my-plan"
              className={styles.paymentButton}
              fullWidth
              disabled={getPlanDisabled}
              onClick={handlePlanSelect}
            >
              {submitButtonText || (
                <FormattedMessage
                  id="Onboarding.Paywall.Main.ActionButton"
                  defaultMessage="Get plan"
                />
              )}
            </UiKitButton>
          )}

          {value &&
            isIntroOfferEnabled &&
            (plan.name !== '12-WEEK PLAN' || variant === 'variant17') && (
              <CardAnnotation
                planName={plan.name}
                fullPriceDiscount={plan.fullPriceDiscount}
                currency={plan.currency}
              />
            )}
        </div>
      )}
    />
  );
});
